import { createContext, useContext, useCallback, useRef } from 'react'
import restClient from 'Shared/hooks/restClient'
import waitForDeferredResult from 'Shared/utils/waitForDeferredResult'
import analyticsReducer from 'AnalyticsDashboard/context/analyticsReducer'
import type { AnalyticsState, QueryState } from 'AnalyticsDashboard/context/analyticsReducer'
import {
  referencedFactTableKinds,
  computeAvailableFilters,
  computeAvailableDimensions,
} from 'AnalyticsDashboard/utils/factTableHelpers'
import type { FactTable, Metric, Filter, Dimension } from 'AnalyticsDashboard/utils/factTableHelpers'

declare const _: any

interface ContextValue {
  state: AnalyticsState
  dispatch: React.Dispatch<any>
}

const AnalyticsContext = createContext<ContextValue | null>(null)

export { AnalyticsContext, analyticsReducer }

export function useAnalyticsContext(): ContextValue {
  const context = useContext(AnalyticsContext)
  if (!context) {
    throw new Error('useAnalyticsContext must be used within AnalyticsProvider')
  }
  return context
}

export function useFilters(): { filters: Filter[]; segmentDimensions: Dimension[] } {
  const { state } = useAnalyticsContext()

  const isGlobal = _.some(_.values(state.queries), (q: QueryState) => q?.global)
  const clientIds = state.filterValues?.client_id || []
  const singleClient = !isGlobal || (Array.isArray(clientIds) && clientIds.length === 1)

  // Collect all fact tables referenced by any registered query's metrics
  const allMetrics: Metric[] = _.flatMap(_.values(state.queries), (q: QueryState) => q?.metrics || [])
  const refKinds = referencedFactTableKinds(allMetrics)

  const filters = computeAvailableFilters(state.queriesInfo, refKinds, singleClient)

  // Segment dimensions: intersection, filtered to kind=segment
  const allDims = computeAvailableDimensions(state.queriesInfo, refKinds)
  const segmentDimensions = allDims.filter(d => d.kind === 'segment')

  return { filters, segmentDimensions }
}

export function useAnalyticsQuery(id: string) {
  const { state, dispatch } = useAnalyticsContext()

  const foundQuery = state.queries[id]
  let query: (QueryState & { queryInfo: { availableDimensions: Dimension[]; availableFilters: Filter[] } }) | null = null

  if (foundQuery) {
    const refKinds = referencedFactTableKinds(foundQuery.metrics || [])
    const availableDimensions = computeAvailableDimensions(state.queriesInfo, refKinds)
    const availableFilters = computeAvailableFilters(state.queriesInfo, refKinds, true)

    query = {
      ...foundQuery,
      queryInfo: { availableDimensions, availableFilters },
    }
  }

  const updateQuery = useCallback((data: Partial<QueryState>) => {
    dispatch({ type: 'UPDATE_QUERY', payload: { id, data } })
  }, [dispatch, id])

  const setQueryData = useCallback((data: any) => {
    dispatch({ type: 'SET_QUERY_DATA', payload: { id, data } })
  }, [dispatch, id])

  const setQueryDataLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_QUERY_DATA_LOADING', payload: { id, loading } })
  }, [dispatch, id])

  const updateDimension = useCallback((dimensionId: string, data: Record<string, any>) => {
    dispatch({ type: 'UPDATE_DIMENSION', payload: { id, dimensionId, data } })
  }, [dispatch, id])

  return {
    query,
    updateQuery,
    updateDimension,
    setQueryData,
    setQueryDataLoading,
  }
}

export function useAnalyticsDashboard() {
  const { state, dispatch } = useAnalyticsContext()
  const { filters, segmentDimensions } = useFilters()

  const fetchQueriesInfo = useCallback(async () => {
    const params = state.global ? { global: true } : {}
    const response = await restClient.get('/api/analytics/analytics_queries/fact_tables', { params })
    dispatch({ type: 'SET_QUERIES_INFO', payload: response.data })
  }, [dispatch, state.global])

  // Track fetch generation per query to discard stale responses
  const fetchGenerationRef = useRef<Record<string, number>>({})

  const fetchDataAll = useCallback(async () => {
    const queryIds = Object.keys(state.queries)
    for (const queryId of queryIds) {
      fetchData(queryId)
    }
  }, [state.queries, state.filterValues, state.global])

  const requiredFiltersMet = state.requiredFilters.every(filterId => {
    const val = state.filterValues[filterId]
    if (val === undefined || val === null) return false
    if (Array.isArray(val) && val.length === 0) return false
    return true
  })

  const fetchData = async (queryId: string) => {
    const query = state.queries[queryId]
    if (!query) return
    if (!requiredFiltersMet) return

    const generation = (fetchGenerationRef.current[queryId] || 0) + 1
    fetchGenerationRef.current[queryId] = generation

    const {
      dimensions = [],
      metrics,
      global: queryGlobal = false,
      overrideFilters = {},
      limit = null,
      orderBy = [],
      comparePeriods = null,
    } = query
    const global = queryGlobal || state.global

    dispatch({
      type: 'UPDATE_QUERY',
      payload: { id: queryId, data: { dataLoading: true, dataError: null, firstFetch: false } }
    })

    try {
      // Convert filterValues from map to array for API
      const filterValues = _.map({ ...state.filterValues, ...overrideFilters }, (value: any, id: string) => {
        return { id, value }
      }).filter((fv: any) => fv.value !== undefined && fv.value !== null && !(Array.isArray(fv.value) && fv.value.length === 0))

      const params = {
        filterValues,
        metrics, // Already in v2 format: [{id, factTable, column, operation}, ...]
        dimensions,
        global,
        orderBy,
        limit,
        ...(comparePeriods ? { comparePeriods } : {})
      }

      const result = await restClient.post('/api/analytics/analytics_queries/run_composite_query', params)
      const deferredResultId = result.data.deferredResultId
      const data = await waitForDeferredResult(deferredResultId)

      // Discard result if a newer fetch was started for this query
      if (fetchGenerationRef.current[queryId] !== generation) return

      dispatch({
        type: 'UPDATE_QUERY',
        payload: { id: queryId, data: { data, dataLoading: false } }
      })
    } catch {
      if (fetchGenerationRef.current[queryId] !== generation) return

      dispatch({
        type: 'UPDATE_QUERY',
        payload: { id: queryId, data: { dataLoading: false, dataError: 'Failed to load data' } }
      })
    }
  }

  const registerQuery = useCallback((payload: { id: string } & Partial<QueryState>) => {
    dispatch({ type: 'REGISTER_QUERY', payload })
  }, [dispatch])

  const unregisterQuery = useCallback((id: string) => {
    dispatch({ type: 'UNREGISTER_QUERY', payload: { id } })
  }, [dispatch])

  const setFilterValue = useCallback((id: string, value: any) => {
    dispatch({ type: 'SET_FILTER', payload: { id, value } })
  }, [dispatch])

  const setTimeDimension = useCallback((value: any) => {
    dispatch({ type: 'SET_TIME_DIMENSION', payload: { value } })
  }, [dispatch])

  const toggleSegmentDimension = useCallback((id: string) => {
    dispatch({ type: 'TOGGLE_SEGMENT_DIMENSION', payload: { id } })
  }, [dispatch])

  return {
    fetchQueriesInfo,
    filters,
    segmentDimensions,
    filterValues: state.filterValues,
    requiredFilters: state.requiredFilters,
    requiredFiltersMet,
    registerQuery,
    unregisterQuery,
    setFilterValue,
    setTimeDimension,
    toggleSegmentDimension,
    fetchData,
    fetchDataAll,
  }
}
